import { type CSSProperties } from 'react'
import { formatCurrencyCompact, formatSignedCurrency } from './cfoFormat'
import type { PacingLineResult, PacingModel, PacingStatus } from './forecastPacing'

const INK = 'var(--ink)'
const MUTED = 'var(--muted)'
const HAIR = 'var(--hair)'
const GOOD = 'var(--good-ink)'
const NEG = 'var(--neg-ink)'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const STATUS_META: Record<PacingStatus, { label: string; color: string; bg: string }> = {
  ahead: { label: 'Ahead', color: GOOD, bg: 'rgba(31, 122, 77, 0.10)' },
  on_track: { label: 'On track', color: INK, bg: 'rgba(15, 23, 42, 0.06)' },
  behind: { label: 'Behind', color: NEG, bg: 'rgba(180, 35, 24, 0.10)' },
  unknown: { label: 'Awaiting sync', color: MUTED, bg: 'rgba(15, 23, 42, 0.04)' },
}

function fmtPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 100)}%`
}

function periodLabel(periodStartIso: string, kind: 'month' | 'week'): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(periodStartIso)
  if (!match) return periodStartIso
  const year = match[1]
  const monthIdx = Math.max(0, Math.min(11, Number(match[2]) - 1))
  if (kind === 'month') return `${MONTHS[monthIdx]} ${year}`
  return `week of ${MONTHS_SHORT[monthIdx]} ${Number(match[3])}`
}

const panelShell: CSSProperties = {
  marginTop: 14,
  background: '#fff',
  border: `1px solid ${HAIR}`,
  borderRadius: 12,
  padding: '16px 18px',
}

const cardShell: CSSProperties = {
  border: `1px solid ${HAIR}`,
  borderRadius: 10,
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  background: 'var(--canvas-2, #fafbfc)',
}

function StatusPill({ status }: { status: PacingStatus }) {
  const meta = STATUS_META[status]
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        padding: '4px 8px',
        borderRadius: 999,
        color: meta.color,
        background: meta.bg,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  )
}

// Tiny pace bar: full track = forecast; tick = where we'd expect to be (elapsed fraction);
// fill = actual share of the full-period forecast. Coloured by status.
function PaceBar({ line, elapsedFraction }: { line: PacingLineResult; elapsedFraction: number }) {
  const color = STATUS_META[line.status].color
  const forecast = line.forecastFull
  const fillRatio = forecast != null && forecast !== 0 && line.actualToDate != null
    ? Math.max(0, Math.min(1.15, line.actualToDate / forecast))
    : null
  const tick = line.kind === 'point_in_time' ? 1 : Math.max(0, Math.min(1, elapsedFraction))
  const width = 100
  const height = 8
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" role="img" aria-hidden>
      <rect x={0} y={2} width={width} height={4} rx={2} fill="rgba(15,23,42,0.08)" />
      {fillRatio != null ? (
        <rect x={0} y={2} width={Math.min(width, fillRatio * width)} height={4} rx={2} fill={color} opacity={0.85} />
      ) : null}
      {line.kind !== 'point_in_time' ? (
        <line x1={tick * width} x2={tick * width} y1={0} y2={height} stroke="rgba(15,23,42,0.45)" strokeWidth={1} strokeDasharray="2 2" />
      ) : null}
    </svg>
  )
}

function LineCard({ line, elapsedFraction }: { line: PacingLineResult; elapsedFraction: number }) {
  const helper = line.kind === 'point_in_time'
    ? `vs ${formatCurrencyCompact(line.forecastFull)} forecast`
    : `vs ${formatCurrencyCompact(line.expectedToDate)} expected by today`
  const hasActual = line.actualToDate != null
  return (
    <div style={cardShell}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>{line.label}</span>
        <StatusPill status={line.status} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: INK, lineHeight: 1.1 }}>
        {hasActual ? formatCurrencyCompact(line.actualToDate) : '—'}
      </div>
      <div style={{ fontSize: 12, color: MUTED }}>{hasActual ? helper : 'No actuals synced yet'}</div>
      <PaceBar line={line} elapsedFraction={elapsedFraction} />
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
        <span style={{ color: STATUS_META[line.status].color, fontWeight: 600 }}>
          {line.varianceToDate != null ? `${formatSignedCurrency(line.varianceToDate)} vs pace` : '—'}
        </span>
        <span style={{ color: MUTED }}>{line.pacePct != null ? `${fmtPct(line.pacePct)} of plan` : ''}</span>
      </div>
    </div>
  )
}

export type ForecastPacingPanelProps = {
  model: PacingModel
  error?: string
}

export function ForecastPacingPanel({ model, error }: ForecastPacingPanelProps) {
  if (!model.available || !model.period || !model.lines.length) return null
  const { period } = model
  const label = periodLabel(period.periodStartIso, period.kind)
  const elapsedPct = Math.round(period.elapsedFraction * 100)
  const context = period.isPeriodComplete
    ? `${label} · period complete`
    : `Day ${period.daysElapsed} of ${period.daysInPeriod} · ${elapsedPct}% of ${label} elapsed`

  return (
    <div style={panelShell}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>{context}</div>
        <div style={{ fontSize: 12, color: MUTED }}>
          {model.asOfActualsLabel ? `Actuals as of ${model.asOfActualsLabel} sync` : ''}
        </div>
      </div>
      {error ? (
        <div style={{ marginTop: 8, fontSize: 12, color: NEG }}>{error}</div>
      ) : null}
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}
      >
        {model.lines.map((line) => (
          <LineCard key={line.id} line={line} elapsedFraction={period.elapsedFraction} />
        ))}
      </div>
      {model.warning ? (
        <div style={{ marginTop: 12, fontSize: 12, color: MUTED }}>{model.warning}</div>
      ) : null}
    </div>
  )
}

export default ForecastPacingPanel
